import { api } from "aldoo-ra/API";

// Create API client for param-io service
const paramAPI = api("param-io");

/**
 * PARAMETER MANAGEMENT ACTIONS
 */

/**
 * Get list of parameters with pagination and search
 * @param {Object} options - Query options
 * @param {number} [options.page] - Page number
 * @param {number} [options.itemsPerPage] - Items per page
 * @param {string} [options.search] - Search term
 * @param {Object} [options.match] - Match criteria
 * @param {Object} [options.sort] - Sort criteria
 * @param {boolean} [options.extended] - Include extended data
 * @returns {Promise<Object>} - API response with parameters list
 */
export const GetParams = async (options = {}) => {
    try {
        const payload = {
            page: options.page || 1,
            itemsPerPage: options.itemsPerPage || 20,
            sort: options.sort || { updated: -1 },
            extended: options.extended || false
        };

        // Only include search OR match, not both
        if (options.search && typeof options.search === 'object') {
            // If search is an object (MongoDB query), use it directly
            payload.search = options.search;
        } else if (options.search && typeof options.search === 'string') {
            // If search is a string, convert to proper search object
            payload.search = {
                "$or": [
                    { "address": { $regex: options.search, $options: "i" } },
                    { "value": { $regex: options.search, $options: "i" } },
                    { "description": { $regex: options.search, $options: "i" } }
                ]
            };
        } else if (options.match && Object.keys(options.match).length > 0) {
            // Only use match if no search is provided and match is not empty
            payload.match = options.match;
        }

        const response = await paramAPI({
            get_params: payload
        });

        if (response.result) {
            return response;
        } else {
            return {
                result: false,
                error: response.error || 'Failed to fetch parameters',
                data: [],
                totalItems: 0
            };
        }
    } catch (error) {
        throw error;
    }
};

/**
 * Get specific parameter by address
 * @param {string} address - Parameter address
 * @returns {Promise<Object>} - API response with parameter details
 */
export const GetParam = async (address) => {
    try {
        const response = await paramAPI({
            get_param: { address }
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Create a new parameter
 * @param {Object} paramData - Parameter data
 * @param {string} paramData.address - Parameter address
 * @param {*} paramData.value - Parameter value
 * @param {string} [paramData.description] - Parameter description
 * @param {string} [paramData.author] - Author name
 * @returns {Promise<Object>} - API response with created parameter
 */
export const CreateParam = async (paramData) => {
    try {
        const response = await paramAPI({
            create_param: paramData
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Update an existing parameter
 * @param {Object} updateData - Update data
 * @param {string} updateData.address - Parameter address
 * @param {*} updateData.value - New value
 * @param {string} [updateData.description] - New description
 * @param {string} [updateData.author] - Author name
 * @returns {Promise<Object>} - API response with updated parameter
 */
export const UpdateParam = async (updateData) => {
    try {
        const response = await paramAPI({
            update_param: updateData
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Rename a parameter (change its address)
 * @param {Object} renameData - Rename data
 * @param {string} renameData.old_address - Current parameter address
 * @param {string} renameData.new_address - New parameter address
 * @param {string} [renameData.author] - Author name
 * @returns {Promise<Object>} - API response with renamed parameter
 */
export const RenameParam = async (renameData) => {
    try {
        const response = await paramAPI({
            rename_param: renameData
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Delete a parameter
 * @param {Object} params - Delete parameters
 * @param {string} params.address - Parameter address to delete
 * @returns {Promise<Object>} - API response
 */
export const DeleteParam = async ({ address }) => {
    try {
        const response = await paramAPI({
            delete_param: { address }
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * HISTORY MANAGEMENT ACTIONS
 */

/**
 * Get parameter change history
 * @param {Object} params - History parameters
 * @param {string} params.address - Parameter address
 * @param {number} [params.page] - Page number
 * @param {number} [params.itemsPerPage] - Items per page
 * @param {Object} [params.sort] - Sort criteria
 * @returns {Promise<Object>} - API response with history entries
 */
export const GetParamHistory = async ({ address, page = 1, itemsPerPage = 20, sort = { timestamp: -1 } }) => {
    try {
        const response = await paramAPI({
            get_param_history: {
                address,
                page,
                itemsPerPage,
                sort
            }
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Clear all history for a parameter
 * @param {Object} params - Clear parameters
 * @param {string} params.address - Parameter address
 * @returns {Promise<Object>} - API response
 */
export const ClearParamHistory = async ({ address }) => {
    try {
        const response = await paramAPI({
            clear_param_history: { address }
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Remove specific history entry
 * @param {Object} params - Remove parameters
 * @param {string} params.address - Parameter address
 * @param {string} params.hash - History entry hash
 * @returns {Promise<Object>} - API response
 */
export const RemoveHistoryItem = async ({ address, hash }) => {
    try {
        const response = await paramAPI({
            remove_history_item: { address, hash }
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Revert parameter to a historical value
 * @param {string} address - Parameter address
 * @param {*} value - Historical value to revert to
 * @param {string} [author] - Author name
 * @returns {Promise<Object>} - API response
 */
export const RevertToHistoryValue = async (address, value, author = "admin") => {
    try {
        const response = await UpdateParam({
            address,
            value,
            author,
            description: `Reverted to historical value`
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * TAG MANAGEMENT ACTIONS
 */

/**
 * Get all available tags from the system
 * @returns {Promise<Object>} - API response with tags array
 */
export const GetParamTags = async () => {
    try {
        const response = await paramAPI({
            get_param_tags: {}
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Update parameter tags
 * @param {Object} params - Tag update parameters
 * @param {string} [params.address] - Single parameter address (for single update)
 * @param {string[]} [params.addresses] - Multiple parameter addresses (for bulk update)
 * @param {string[]} params.tags - Array of tags to apply
 * @param {string} [params.tagOperation='replace'] - Operation mode: 'append' or 'replace'
 * @returns {Promise<Object>} - API response with update results
 */
export const UpdateParamTags = async ({ address, addresses, tags, tagOperation = 'replace' }) => {
    try {
        const payload = {
            tags,
            tagOperation
        };

        if (address) {
            payload.address = address;
        } else if (addresses) {
            payload.addresses = addresses;
        } else {
            throw new Error('Either address or addresses must be provided');
        }

        const response = await paramAPI({
            update_param_tags: payload
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * UTILITY ACTIONS
 */

/**
 * Get system statistics
 * @returns {Promise<Object>} - API response with statistics
 */
export const GetStatistics = async () => {
    try {
        const response = await paramAPI({
            get_statistics: {}
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Get list of all unique authors who have created or modified parameters
 * @returns {Promise<Object>} - API response with authors list
 */
export const GetAuthors = async () => {
    try {
        const response = await paramAPI({
            get_authors: {}
        });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Search parameters with flexible criteria
 * @param {Object} criteria - Search criteria
 * @param {string} [criteria.search] - Text search
 * @param {string} [criteria.author] - Filter by author
 * @param {string} [criteria.type] - Filter by value type
 * @returns {Promise<Object>} - API response with search results
 */
export const SearchParams = async (criteria) => {
    const options = {};
    
    // Build search query if text search is provided
    if (criteria.search) {
        const searchFilters = [
            { "address": { $regex: criteria.search, $options: "i" } },
            { "value": { $regex: criteria.search, $options: "i" } },
            { "description": { $regex: criteria.search, $options: "i" } }
        ];
        
        const additionalFilters = [];
        
        // Add author filter if provided
        if (criteria.author) {
            additionalFilters.push({ "author": criteria.author });
        }
        
        // Add type filter if provided
        if (criteria.type) {
            switch (criteria.type) {
                case 'string':
                    additionalFilters.push({ "value": { $type: 'string' } });
                    break;
                case 'number':
                    additionalFilters.push({ "value": { $type: 'number' } });
                    break;
                case 'boolean':
                    additionalFilters.push({ "value": { $type: 'boolean' } });
                    break;
                case 'object':
                    additionalFilters.push({ "value": { $type: 'object' } });
                    break;
                case 'array':
                    additionalFilters.push({ "value": { $type: 'array' } });
                    break;
            }
        }
        
        // Combine search with additional filters
        if (additionalFilters.length > 0) {
            options.search = {
                "$and": [
                    { "$or": searchFilters },
                    ...additionalFilters
                ]
            };
        } else {
            options.search = { "$or": searchFilters };
        }
    } else {
        // No text search, use match for filters only
        const matchFilters = {};
        
        if (criteria.author) {
            matchFilters.author = criteria.author;
        }
        
        if (criteria.type) {
            switch (criteria.type) {
                case 'string':
                    matchFilters.value = { $type: 'string' };
                    break;
                case 'number':
                    matchFilters.value = { $type: 'number' };
                    break;
                case 'boolean':
                    matchFilters.value = { $type: 'boolean' };
                    break;
                case 'object':
                    matchFilters.value = { $type: 'object' };
                    break;
                case 'array':
                    matchFilters.value = { $type: 'array' };
                    break;
            }
        }
        
        if (Object.keys(matchFilters).length > 0) {
            options.match = matchFilters;
        }
    }

    return GetParams(options);
};

/**
 * Get parameter value types for filtering
 * @param {*} value - Parameter value
 * @returns {string} - Value type
 */
export const GetValueType = (value) => {
    if (value === null) return 'null';
    if (Array.isArray(value)) return 'array';
    return typeof value;
};

/**
 * Validate parameter address format
 * @param {string} address - Parameter address
 * @returns {boolean} - True if valid
 */
export const ValidateAddress = (address) => {
    if (!address || typeof address !== 'string') return false;
    
    // Must start/end with alphanumeric
    if (!/^[a-zA-Z0-9].*[a-zA-Z0-9]$/.test(address) && address.length > 1) return false;
    if (address.length === 1 && !/^[a-zA-Z0-9]$/.test(address)) return false;
    
    // Can contain letters, numbers, underscores, dots
    if (!/^[a-zA-Z0-9_.]+$/.test(address)) return false;
    
    // No consecutive dots
    if (address.includes('..')) return false;
    
    return true;
};

/**
 * Format parameter value for display
 * @param {*} value - Parameter value
 * @returns {string} - Formatted value
 */
export const FormatValue = (value) => {
    if (value === null) return 'null';
    if (value === undefined) return 'undefined';
    if (typeof value === 'string') return value;
    if (typeof value === 'boolean') return value.toString();
    if (typeof value === 'number') return value.toString();
    if (Array.isArray(value) || typeof value === 'object') {
        return JSON.stringify(value, null, 2);
    }
    return String(value);
};

/**
 * Parse value from string input
 * @param {string} input - String input
 * @param {string} type - Expected type
 * @returns {*} - Parsed value
 */
export const ParseValue = (input, type) => {
    if (type === 'string') return input;
    if (type === 'number') return Number(input);
    if (type === 'boolean') return input === 'true';
    if (type === 'object' || type === 'array') {
        try {
            return JSON.parse(input);
        } catch (e) {
            throw new Error('Invalid JSON format');
        }
    }
    return input;
};

// Export all functions as default object
export default {
    // Parameter CRUD
    GetParams,
    GetParam,
    CreateParam,
    UpdateParam,
    RenameParam,
    DeleteParam,
    
    // History management
    GetParamHistory,
    ClearParamHistory,
    RemoveHistoryItem,
    RevertToHistoryValue,
    
    // Tag management
    GetParamTags,
    UpdateParamTags,
    
    // Utilities
    GetStatistics,
    GetAuthors,
    SearchParams,
    GetValueType,
    ValidateAddress,
    FormatValue,
    ParseValue
};